/* eslint-disable @typescript-eslint/require-await */
import {
  applyStateUpdate,
  UnreachableError,
  validateIsRecord,
  type ChildBridge,
  type GamePlayer,
  type PlayerDataUpdate,
  type SdkMessageType,
  type SdkMessageByType,
  type UpdatePlayerDataMessage,
  type AckUpdatePlayerDataMessage,
  type CreateLinkResponseMessage,
  type LoginLeaseAcquiredMessage,
  type SetPlayerMessage,
  checkMessageKeywordMatch,
  type OnboardingAnalyticsEvent,
  validOnboardingEvents,
  type ScheduleNotificationV2Message,
  type BeginPlatformPurchaseResponseMessage,
} from "@textclub/common";
import { IFrameChildBridge } from "./bridge";
import { MockBridge } from "./mock-bridge";
import { DebugMenu } from "./debug-menu";
import deepmerge from "@fastify/deepmerge";
import type {
  GetLinkResponse,
  GetLoginTokenResponse,
  LoginReservation,
  TextclubSDK,
} from ".";
import { AsyncMessagingHelper } from "./async-messaging-helper";

const Duration = {
  MINUTE: 60 * 1000,
  SECOND: 1000,
} as const;
const LOGIN_REQUEST_TIMEOUT = 10 * Duration.SECOND;
const READY_TIMEOUT = 60 * Duration.SECOND;

type MessageHandlers = {
  [K in SdkMessageType]: (message: SdkMessageByType<K>) => Promise<unknown>;
};

/**
 * The main SDK class for integrating games with the Textclub platform.
 * Handles player management, notifications, and analytics within an iframe context.
 * @example
 * ```typescript
 * const sdk = new Sdk();
 * await sdk.isReady();
 * sdk.login("Welcome! Your code is {{registrationCode}}", "Register to Play");
 * ```
 */

// Use this to flip our unintialized sdk to an initialized sdk
interface Initialized {
  _player: GamePlayer;
}

export const GetLoginLinkResponseErrors = {
  COULD_NOT_ACQUIRE_LEASE: "could_not_acquire_lease",
} as const;

export class Sdk implements TextclubSDK {
  private resolveReady: () => void = () => {};
  private rejectReady: (error: Error) => void = () => {};
  private readyPromise: Promise<void>;
  private initMessageInterval: number;
  private readyTimeoutHandle: number;
  private initialPlayerData: Record<string, unknown>;
  private bridge: IFrameChildBridge;
  private updateCounter: number = 0;
  // I couldn't get the typeguard to work on our player object without making this public
  _player: GamePlayer | null = null;

  private pendingUpdates: Array<PlayerDataUpdate & { updateId: number }> = [];
  private unackedUpdates: Array<UpdatePlayerDataMessage> = [];

  private asyncBridgeHelper: AsyncMessagingHelper;

  private pendingFlushPromises: Array<{
    id: number;
    resolve: () => void;
  }> = [];

  // Internal API specific state
  private tokenToLinkMap: Map<string, string> = new Map();

  /**
   * Creates an instance of the SDK with an optional bridge for communication.
   * Automatically starts initialization by sending "Initialized" messages to the parent window.
   * @param opts - Configuration options for the SDK.
   * @param opts.initialPlayerData - The initial player state to use if player is uninitialized. Defaults to an empty object.
   */
  constructor(
    opts: { initialPlayerData?: Record<string, unknown> } = {},
    clientOpts?: {
      bridge?: ChildBridge;
    },
  ) {
    this.bridge = clientOpts?.bridge || new IFrameChildBridge();
    this.asyncBridgeHelper = new AsyncMessagingHelper();

    this.initialPlayerData = opts.initialPlayerData || {};

    this.initMessageInterval = setInterval(() => {
      this.bridge.sendMessage({ type: "Initialized" });
    }, 50);

    this.readyPromise = new Promise((resolve, reject) => {
      this.resolveReady = resolve;
      this.rejectReady = reject;
    });

    this.readyTimeoutHandle = setTimeout(() => {
      this.rejectReady(new Error("Timeout waiting for player init"));
      clearInterval(this.initMessageInterval);
    }, READY_TIMEOUT);

    this.bridge.onMessage = (message) => {
      this.handleMessage(message).catch((error) => {
        console.error("Error handling message", message, error);
      });
    };
  }

  private async handleMessage<T extends SdkMessageType>(
    message: SdkMessageByType<T>,
  ): Promise<boolean> {
    const handler = this.messageHandlers[message.type];

    if (!handler) {
      return false;
    }

    const result = handler(message);
    if (result instanceof Promise) {
      await result;
    }

    return true;
  }

  messageHandlers: MessageHandlers = {
    SetPlayer: async (message: SetPlayerMessage) => {
      this.initPlayer(message.player);
    },
    AckUpdatePlayerData: async (message: AckUpdatePlayerDataMessage) => {
      if (this.unackedUpdates.length > 1) {
        // this is currently an unexpected case, update pushing is currently synchronous
        // so we should never have more than one unacked update
        throw new Error("Unexpected multiple unacked updates");
      }
      if (message.id !== this.unackedUpdates[0]?.id) {
        throw new Error("Unexpected ack id");
      }
      this.unackedUpdates.shift(); // this technically doesn't need to be an array since its sync

      // resolve any pending flush promises that have an id less than or equal to the ack id
      for (const { id, resolve } of this.pendingFlushPromises) {
        if (id <= message.id) {
          resolve();
        }
      }

      this.flushUpdates();
    },
    LoginLeaseAcquired: async (message: LoginLeaseAcquiredMessage) => {
      this.asyncBridgeHelper.resolveWaiting(message);
    },
    CreateLinkResponse: async (message: CreateLinkResponseMessage) => {
      this.asyncBridgeHelper.resolveWaiting(message);
    },
    GetGameProductsResponse: async (message) => {
      this.asyncBridgeHelper.resolveWaiting(message);
    },
    BeginPlatformPurchaseResponse: async (
      message: BeginPlatformPurchaseResponseMessage,
    ) => {
      this.asyncBridgeHelper.resolveWaiting(message);
    },
  };

  private initPlayer(player: GamePlayer) {
    clearTimeout(this.readyTimeoutHandle);
    clearInterval(this.initMessageInterval);
    this._player = player;
    // init player state with defaults if not set - using deep merge
    this._player.playerData = deepmerge({
      // if target is set, prefer it in array merging
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      mergeArray: (options) => (target, _source) => options.clone(target),
    })(this.initialPlayerData, this._player.playerData);
    this.setPlayerData(this._player.playerData);
    this.resolveReady();

    // Set up first interaction listener
    this.setupFirstInteractionListener();
  }

  private setupFirstInteractionListener() {
    const firstInteractionHandler = (_event: MouseEvent | TouchEvent) => {
      // Send first interaction event to host
      this.bridge.sendMessage({
        type: "OnboardingCaptureEvent",
        event: "first_interaction",
      });

      // Remove the event listeners
      document.removeEventListener("click", firstInteractionHandler);
      document.removeEventListener("touchstart", firstInteractionHandler);
    };

    // Add listeners for both mouse and touch events with once: true
    document.addEventListener("click", firstInteractionHandler, { once: true });
    document.addEventListener("touchstart", firstInteractionHandler, {
      once: true,
    });
  }

  private ensureInitialized(): asserts this is Initialized {
    if (!this._player || this._player === null) {
      throw new Error(
        "Player not initialized, await `isReady()` before doing anything.",
      );
    }
  }

  getEntryPayload(): Record<string, unknown> {
    this.ensureInitialized();

    return this._player.entryPayload;
  }

  getPlayer(): GamePlayer {
    this.ensureInitialized();
    return {
      playerId: this._player.playerId,
      registered: this._player.registered,
      entryPayload: this._player.entryPayload,
      playerData: this._player.playerData,
    };
  }

  getPlayerDataVal(key: string): unknown {
    this.ensureInitialized();

    if (typeof key !== "string") {
      throw new Error("INVALID_ARGUMENTS");
    }

    return this._player.playerData?.[key];
  }

  getPlayerData(): Record<string, unknown> {
    this.ensureInitialized();
    return { ...this._player.playerData };
  }

  setPlayerDataVal(key: string, value: unknown): void {
    this.ensureInitialized();

    if (typeof key !== "string") {
      throw new Error("INVALID_ARGUMENTS");
    }

    this.setPlayerData({ [key]: value });
  }

  setPlayerData(partial: Record<string, unknown>): void {
    this.ensureInitialized();

    if (!validateIsRecord(partial)) {
      throw new Error("INVALID_ARGUMENTS");
    }

    this.updateCounter++;
    const update = { update: partial };
    this._player.playerData = applyStateUpdate(this._player.playerData, update);
    this.pendingUpdates.push({
      update: update.update,
      updateId: this.updateCounter,
    });
    this.flushUpdates();
  }

  /**
   * Sends any updates that haven't been acknowledged by the host
   */
  private flushUpdates() {
    // reduce pending updates into a single update
    if (this.unackedUpdates.length > 0) {
      return;
    }

    if (this.pendingUpdates.length === 0) {
      return;
    }

    const maxUpdateId = this.pendingUpdates[this.pendingUpdates.length - 1];
    if (maxUpdateId === undefined) {
      throw new UnreachableError("maxUpdateId should be defined");
    }

    // collapse any pending updates into a single message
    const collapsedPlayerDataUpdate = this.pendingUpdates.reduce((acc, cur) => {
      return { ...acc, ...cur.update };
    }, {});

    const updateMessage: UpdatePlayerDataMessage = {
      type: "UpdatePlayerData",
      id: maxUpdateId.updateId,
      update: { update: collapsedPlayerDataUpdate },
    };

    // send the updates
    this.unackedUpdates.push(updateMessage);
    this.pendingUpdates = [];
    this.bridge.sendMessage(updateMessage);
  }

  static mock(opts: { initialPlayerData?: Record<string, unknown> } = {}) {
    const mockBridge = new MockBridge();
    new DebugMenu(mockBridge);
    return new Sdk(opts, { bridge: mockBridge });
  }

  /**
   * Waits for the SDK to be fully initialized with player data.
   * @returns A promise that resolves when the player is set.
   * @throws {Error} If initialization times out after 1 second.
   */
  isReady() {
    return this.readyPromise;
  }

  login(opts: {
    message: string;
    title: string;
    entryPayload?: Record<string, unknown>;
  }) {
    if (
      typeof opts !== "object" ||
      typeof opts.message !== "string" ||
      typeof opts.title !== "string" ||
      (opts.entryPayload && !validateIsRecord(opts.entryPayload))
    ) {
      throw new Error("INVALID_ARGUMENTS");
    }

    this.ensureInitialized();
    if (this._player.registered) {
      throw new Error("Player already logged in");
    }

    const { message } = opts;

    if (!message.includes("{{registrationCode}}")) {
      throw new Error("INVALID_ARGUMENTS");
    }
    this.bridge.sendMessage({
      type: "BeginPlatformLogin",
      entryPayload: JSON.stringify(opts.entryPayload),
    });
  }

  getLink(options: {
    gameSlug: string;
    entryPayload?: Record<string, unknown>;
  }): Promise<GetLinkResponse> {
    if (
      typeof options !== "object" ||
      typeof options.gameSlug !== "string" ||
      (options.entryPayload && !validateIsRecord(options.entryPayload))
    ) {
      throw new Error("INVALID_ARGUMENTS");
    }

    const { gameSlug, entryPayload } = options;
    this.ensureInitialized();

    const conversationId = self.crypto.randomUUID();

    // init sending link request
    this.bridge.sendMessage({
      type: "CreateLink",
      gameSlug,
      entryPayload: JSON.stringify(entryPayload),
      conversationId: conversationId,
    });

    // Still returning this as a promise (from this non-async method)
    // to maintain compatibility with existing calls to this method.
    // Allows input validation to happen outside a promise reject.
    return this.asyncBridgeHelper
      .waitForNextMessage(
        "CreateLinkResponse",
        conversationId,
        LOGIN_REQUEST_TIMEOUT,
      )
      .then((response) => ({ link: response.link }));
  }

  scheduleNotification(options: {
    scheduledAt: Date;
    message: string;
    attemptPushNotification: boolean;
    identifier?: string;
    entryPayload?: Record<string, unknown>;
    deduplicationKey?: string; // deprecated 2025-05-22; keep around until we update the Unity SDK
  }) {
    if (
      typeof options !== "object" ||
      !(options.scheduledAt instanceof Date) ||
      typeof options.message !== "string" ||
      typeof options.attemptPushNotification !== "boolean" ||
      (options.identifier && typeof options.identifier !== "string") ||
      (options.entryPayload && !validateIsRecord(options.entryPayload)) ||
      (options.deduplicationKey && typeof options.deduplicationKey !== "string")
    ) {
      throw new Error("INVALID_ARGUMENTS");
    }

    if (!this._player) {
      throw new Error("Player not initialized, await `isReady()` first.");
    }

    if (options.deduplicationKey && options.identifier) {
      throw new Error(
        "Property deduplicationKey is deprecated, use identifier instead.",
      );
    }

    this.bridge.sendMessage({
      ...options,
      type: "ScheduleNotification",
      scheduledAt: options.scheduledAt.toISOString(),
      identifier: options.identifier || options.deduplicationKey,
      entryPayload: options.entryPayload,
    });
  }

  unscheduleNotification(options: { identifier: string }) {
    if (typeof options !== "object" || typeof options.identifier !== "string") {
      throw new Error("INVALID_ARGUMENTS");
    }
    if (!this._player) {
      throw new Error("Player not initialized, await `isReady()` first.");
    }
    this.bridge.sendMessage({
      type: "UnscheduleNotification",
      identifier: options.identifier,
    });
  }

  captureEvent(eventName: string, properties?: Record<string, unknown>) {
    if (
      typeof eventName !== "string" ||
      (properties && typeof properties !== "object")
    ) {
      throw new Error("INVALID_ARGUMENTS");
    }

    this.bridge.sendMessage({
      type: "CaptureEvent",
      event: eventName,
      properties: properties || {},
    });
  }

  redirectToFlagshipGame(options?: {
    entryPayload?: Record<string, unknown>;
  }): void {
    if (
      (options !== undefined && typeof options !== "object") ||
      options === null
    ) {
      throw new Error("INVALID_ARGUMENTS");
    }

    let entryPayloadString: string | undefined;

    if (options?.entryPayload !== undefined) {
      if (!validateIsRecord(options.entryPayload)) {
        throw new Error("INVALID_ARGUMENTS");
      }
      entryPayloadString = JSON.stringify(options.entryPayload);
    }

    this.bridge.sendMessage({
      type: "RedirectToGame",
      redirectToFlagship: true,
      entryPayload: entryPayloadString,
    });
  }

  redirectToGame(options: {
    gameSlug: string;
    entryPayload?: Record<string, unknown>;
  }): void {
    if (
      typeof options !== "object" ||
      typeof options.gameSlug !== "string" ||
      (options.entryPayload && !validateIsRecord(options.entryPayload))
    ) {
      throw new Error("INVALID_ARGUMENTS");
    }

    this.bridge.sendMessage({
      type: "RedirectToGame",
      gameSlug: options.gameSlug,
      entryPayload: JSON.stringify(options.entryPayload),
    });
  }

  openPrivacyPolicy() {
    this.bridge.sendMessage({
      type: "OpenLegalPage",
      page: "privacy",
    });
  }

  openTermsOfService() {
    this.bridge.sendMessage({
      type: "OpenLegalPage",
      page: "terms",
    });
  }

  openCopyright() {
    this.bridge.sendMessage({
      type: "OpenLegalPage",
      page: "copyright",
    });
  }

  flush(): Promise<void> {
    if (this.pendingUpdates.length === 0 && this.unackedUpdates.length === 0) {
      return Promise.resolve();
    }
    const flushPromise = new Promise<void>((resolve) => {
      this.pendingFlushPromises.push({ id: this.updateCounter, resolve });
    });
    return flushPromise;
  }

  notifications = {
    scheduleNotification: (options: {
      scheduledAt: Date;
      image: string; // Base64 data URL for the notification image
      fallbackBody: string;
      body: string;
      ctaText: string;
      priority?: ScheduleNotificationV2Message["priority"];
      identifier?: string;
      entryPayload?: Record<string, unknown>;
    }) => {
      if (
        typeof options !== "object" ||
        !(options.scheduledAt instanceof Date) ||
        typeof options.image !== "string" ||
        typeof options.fallbackBody !== "string" ||
        typeof options.body !== "string" ||
        typeof options.ctaText !== "string" ||
        (options.priority &&
          !["low", "medium", "high", "critical"].includes(options.priority)) ||
        !options.identifier ||
        typeof options.identifier !== "string" ||
        !options.entryPayload ||
        !validateIsRecord(options.entryPayload)
      ) {
        throw new Error("INVALID_ARGUMENTS");
      }

      // Validate data URL format
      // Data URLs must start with "data:" and include a MIME type and base64 encoding
      const dataUrlRegex =
        /^data:([a-zA-Z0-9]+\/[a-zA-Z0-9\-.+]+);base64,(.+)$/;
      if (!dataUrlRegex.test(options.image)) {
        throw new Error(
          "INVALID_DATA_URL: Image must be a valid base64 data URL (e.g., data:image/png;base64,...)",
        );
      }

      this.ensureInitialized();

      this.bridge.sendMessage({
        type: "ScheduleNotificationV2",
        scheduledAt: options.scheduledAt.toISOString(),
        image: options.image,
        fallbackBody: options.fallbackBody,
        body: options.body,
        ctaText: options.ctaText,
        priority: options.priority || "low",
        identifier: options.identifier,
        entryPayload: options.entryPayload,
      });
    },
    unscheduleNotification: (options: { identifier: string }) => {
      this.unscheduleNotification(options);
    },
  };

  payments = {
    beginPurchase: async (options: {
      productSku: string;
    }): ReturnType<TextclubSDK["payments"]["beginPurchase"]> => {
      if (
        !options ||
        typeof options !== "object" ||
        typeof options.productSku !== "string"
      ) {
        throw new Error("INVALID_ARGUMENTS");
      }

      this.ensureInitialized();
      if (!this._player.registered) {
        throw new Error("Player must be registered to make purchases");
      }

      const conversationId = `beginPurchase_${Date.now()}_${Math.random().toString(36).substring(2)}`;
      this.bridge.sendMessage({
        conversationId,
        type: "BeginPlatformPurchase",
        productSku: options.productSku,
      });

      // For now, return a promise that resolves immediately with a mock token
      // In a real implementation, this would wait for a response from the host
      const response = await this.asyncBridgeHelper.waitForNextMessage(
        "BeginPlatformPurchaseResponse",
        conversationId,
        5 * Duration.MINUTE,
      );
      switch (response.result) {
        case "error":
          return { result: "error", error: response.error };
        case "cancel":
          return { result: "cancel" };
        case "success":
          return { result: "success", purchaseToken: response.purchaseToken };
      }
    },

    getProducts: async (): Promise<
      Array<{
        sku: string;
        name: string;
        description: string | null;
        price: number;
      }>
    > => {
      this.ensureInitialized();

      // Generate a unique conversation ID for this request
      const conversationId = `getProducts_${Date.now()}_${Math.random().toString(36).substring(2)}`;

      // Send the GetGameProducts message
      this.bridge.sendMessage({
        type: "GetGameProducts",
        conversationId,
      });

      // Wait for the response with a 10 second timeout
      const response = await this.asyncBridgeHelper.waitForNextMessage(
        "GetGameProductsResponse",
        conversationId,
        10 * Duration.SECOND,
      );

      if (!response.success) {
        throw new Error("Failed to fetch products");
      }

      return response.products;
    },
  };

  internal = {
    reserveLoginMessageAsync: (options: {
      message: string;
      keywords?: string[];
      entryPayload?: Record<string, unknown>;
    }): Promise<GetLoginTokenResponse> => {
      if (
        !options ||
        typeof options !== "object" ||
        typeof options.message !== "string" ||
        (options.keywords !== undefined && !Array.isArray(options.keywords)) ||
        (options.entryPayload && !validateIsRecord(options.entryPayload))
      ) {
        throw new Error("INVALID_ARGUMENTS");
      }

      if (
        options.keywords &&
        !checkMessageKeywordMatch(options.message, options.keywords)
      ) {
        throw new Error(
          "INVALID_ARGUMENTS: Keywords must be present in message in same order",
        );
      }

      this.ensureInitialized();
      if (this._player.registered) {
        throw new Error("Player already logged in");
      }

      const { message, keywords, entryPayload } = options;
      const conversationId: string = self.crypto.randomUUID();

      this.bridge.sendMessage({
        type: "AcquireLoginLease",
        message,
        keywords: keywords ?? null,
        entryPayload: JSON.stringify(entryPayload),
        conversationId: conversationId,
      });

      // Still returning this as a promise (from this non-async method)
      // to maintain compatibility with existing calls to this method.
      // Allows input validation to happen outside a promise reject.
      return this.asyncBridgeHelper
        .waitForNextMessage(
          "LoginLeaseAcquired",
          conversationId,
          LOGIN_REQUEST_TIMEOUT,
        )
        .then((response) => {
          if (!response.success) {
            switch (response.error) {
              case "internal_error":
                return {
                  error: GetLoginLinkResponseErrors.COULD_NOT_ACQUIRE_LEASE,
                };
            }
          } else {
            // Generate a random token -- doesn't need to be cryptographically secure
            const token = `${Date.now().toString(36)}${Math.random().toString(36).substring(2, 8)}`;
            this.tokenToLinkMap.set(token, response.link);
            const reservation: LoginReservation = {
              __type: "LoginReservation",
              __token: token,
            };
            return { reservation };
          }
        });
    },

    sendReservedLoginMessage: (reservation: LoginReservation): void => {
      if (
        !reservation ||
        typeof reservation !== "object" ||
        reservation.__type !== "LoginReservation" ||
        typeof reservation.__token !== "string"
      ) {
        throw new Error("INVALID_ARGUMENTS");
      }

      // Look up the link from the token
      const link = this.tokenToLinkMap.get(reservation.__token);
      if (!link) {
        throw new Error("Invalid or expired reservation");
      }

      // Don't remove the token - allow multiple uses
      this.bridge.sendMessage({
        type: "OnboardingCaptureEvent",
        event: "sms_button_tap",
      });

      window.parent.location.href = link;
    },

    captureOnboardingEvent: (event: OnboardingAnalyticsEvent) => {
      // Validate event.
      //
      if (!validOnboardingEvents.includes(event)) {
        throw new Error(
          "INVALID_ARGUMENTS: Unknown onboarding event: " + event,
        );
      }

      this.ensureInitialized();
      this.bridge.sendMessage({
        type: "OnboardingCaptureEvent",
        event: event,
      });
    },
  };
}
